import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Box, Typography, Select, MenuItem, IconButton, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Checkbox, FormControlLabel, Tooltip, Autocomplete, CircularProgress,
} from '@mui/material';
import {
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  Today as TodayIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/rpc';
import { gwtStyles } from '../../config/theme';
import { pushError, pushSuccess } from '../../stores/notificationStore';
import { ConfirmDialog } from '../../shared/components/Dialogs';
import type { ScheduleEntry, PatientRecord } from '../../api/types';

const HOURS = Array.from({ length: 14 }, (_, i) => i + 6); // 06:00 - 19:00
const MODALITIES = ['ALL', 'CT', 'MR', 'XR', 'US', 'NM', 'MG', 'PET', 'DEXA'];

/**
 * Scheduling panel — DaySchedulePanel equivalent.
 * Day-view calendar with time slots, scheduled exams, and CRUD actions.
 */
export const SchedulingPanel = () => {
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    return d.toISOString().split('T')[0];
  });
  const [modality, setModality] = useState('ALL');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<ScheduleEntry | null>(null);
  const [unscheduleTarget, setUnscheduleTarget] = useState<ScheduleEntry | null>(null);
  const [rescheduleTarget, setRescheduleTarget] = useState<ScheduleEntry | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);

  const { data: entries, isLoading } = useQuery<ScheduleEntry[]>({
    queryKey: ['schedule', selectedDate, modality],
    queryFn: async () => {
      try {
        const result = await api.getSchedule(
          selectedDate,
          undefined,
          modality === 'ALL' ? undefined : modality,
        );
        return result.records ?? [];
      } catch {
        return [];
      }
    },
    staleTime: 30_000,
  });

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['schedule'] });
  }, [queryClient]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(refresh, 15_000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, refresh]);

  const navigateDay = useCallback(
    (offset: number) => {
      const d = new Date(selectedDate);
      d.setDate(d.getDate() + offset);
      setSelectedDate(d.toISOString().split('T')[0]);
    },
    [selectedDate],
  );

  const goToday = useCallback(() => {
    setSelectedDate(new Date().toISOString().split('T')[0]);
  }, []);

  const handleUnschedule = useCallback(async () => {
    if (!unscheduleTarget) return;
    try {
      await api.unscheduleOrder(unscheduleTarget.objKey ?? unscheduleTarget.id);
      pushSuccess('Order unscheduled');
      refresh();
    } catch (e) {
      pushError(`Failed to unschedule: ${e instanceof Error ? e.message : String(e)}`);
    }
    setUnscheduleTarget(null);
  }, [unscheduleTarget, refresh]);

  // Group entries by hour
  const entriesByHour = useMemo(() => {
    const map = new Map<number, ScheduleEntry[]>();
    for (const entry of entries ?? []) {
      const hour = parseInt(entry.time?.split(':')[0] ?? '0', 10);
      if (!map.has(hour)) map.set(hour, []);
      map.get(hour)!.push(entry);
    }
    return map;
  }, [entries]);

  const dateLabel = useMemo(() => {
    const d = new Date(selectedDate + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  }, [selectedDate]);

  const totalEntries = entries?.length ?? 0;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: gwtStyles.tabContent }}>
      {/* Title */}
      <Box sx={{ height: 32, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 600, color: gwtStyles.tabTitle }}>
          Schedule
        </Typography>
      </Box>

      {/* Controls */}
      <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', px: 1, py: 0.25, gap: 0.5, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
        <IconButton size="small" onClick={() => navigateDay(-1)} sx={{ width: 28, height: 28 }}>
          <PrevIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <TextField
          size="small" type="date" value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          sx={{ width: 150, '& .MuiInputBase-root': { fontSize: 12, height: 28 } }}
        />
        <IconButton size="small" onClick={() => navigateDay(1)} sx={{ width: 28, height: 28 }}>
          <NextIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <IconButton size="small" onClick={goToday} title="Today" sx={{ width: 28, height: 28 }}>
          <TodayIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Typography variant="body2" sx={{ fontSize: 12, flex: 1, textAlign: 'center', color: 'text.secondary' }}>
          {dateLabel} — {totalEntries} appointment{totalEntries !== 1 ? 's' : ''}
        </Typography>
        <Select
          size="small" value={modality}
          onChange={(e) => setModality(e.target.value)}
          sx={{ fontSize: 12, height: 28, width: 100 }}
        >
          {MODALITIES.map((m) => (
            <MenuItem key={m} value={m} sx={{ fontSize: 12 }}>{m === 'ALL' ? 'All' : m}</MenuItem>
          ))}
        </Select>
        <FormControlLabel
          control={<Checkbox size="small" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} sx={{ p: 0.25 }} />}
          label={<Typography sx={{ fontSize: 10 }}>Auto</Typography>}
          sx={{ ml: 0 }}
        />
        <IconButton size="small" onClick={refresh} title="Refresh" sx={{ width: 28, height: 28 }}>
          <RefreshIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Tooltip title="Add Appointment">
          <Button size="small" variant="outlined" startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            onClick={() => setShowAddDialog(true)}
            sx={{ textTransform: 'none', fontSize: 11, height: 28, ml: 0.5 }}>
            Add
          </Button>
        </Tooltip>
      </Box>

      {/* Schedule grid */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 1, py: 0.5 }}>
        {isLoading ? (
          <Typography variant="body2" sx={{ p: 2, textAlign: 'center', color: 'text.secondary', fontSize: 12 }}>
            Loading schedule...
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderColor: 'divider' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: 11, fontWeight: 600, width: 60, py: 0.5 }}>Time</TableCell>
                  <TableCell sx={{ fontSize: 11, fontWeight: 600, py: 0.5 }}>Patient</TableCell>
                  <TableCell sx={{ fontSize: 11, fontWeight: 600, width: 60, py: 0.5 }}>Mod</TableCell>
                  <TableCell sx={{ fontSize: 11, fontWeight: 600, py: 0.5 }}>Description</TableCell>
                  <TableCell sx={{ fontSize: 11, fontWeight: 600, width: 80, py: 0.5 }}>Status</TableCell>
                  <TableCell sx={{ fontSize: 11, fontWeight: 600, width: 70, py: 0.5 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {HOURS.map((hour) => {
                  const hourEntries = entriesByHour.get(hour) ?? [];
                  const timeStr = `${String(hour).padStart(2, '0')}:00`;
                  if (hourEntries.length === 0) {
                    return (
                      <TableRow key={hour} sx={{ '&:nth-of-type(odd)': { backgroundColor: gwtStyles.gridRowOdd } }}>
                        <TableCell sx={{ fontSize: 11, py: 0.5, color: 'text.secondary' }}>{timeStr}</TableCell>
                        <TableCell colSpan={5} sx={{ fontSize: 11, py: 0.5, color: 'text.disabled' }}>—</TableCell>
                      </TableRow>
                    );
                  }
                  return hourEntries.map((entry, idx) => (
                    <TableRow
                      key={entry.id}
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: gwtStyles.gridRowOdd },
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                      onClick={() => setSelectedEntry(entry)}
                    >
                      <TableCell sx={{ fontSize: 11, py: 0.25 }}>{idx === 0 ? timeStr : ''}</TableCell>
                      <TableCell sx={{ fontSize: 11, py: 0.25 }}>{entry.patientName}</TableCell>
                      <TableCell sx={{ fontSize: 11, py: 0.25 }}>
                        <Chip label={entry.modality ?? '—'} size="small" sx={{ fontSize: 9, height: 16 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, py: 0.25 }}>{entry.description ?? '—'}</TableCell>
                      <TableCell sx={{ fontSize: 11, py: 0.25 }}>
                        <Chip
                          label={entry.status ?? '—'}
                          size="small"
                          color={entry.status === 'COMPLETED' ? 'success' : entry.status === 'INPROGRESS' ? 'info' : 'default'}
                          sx={{ fontSize: 9, height: 16 }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.25 }}>
                        <Box sx={{ display: 'flex', gap: 0.25 }}>
                          <Tooltip title="Reschedule">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setRescheduleTarget(entry); }} sx={{ width: 20, height: 20 }}>
                              <EditIcon sx={{ fontSize: 12 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Unschedule">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setUnscheduleTarget(entry); }} sx={{ width: 20, height: 20 }}>
                              <DeleteIcon sx={{ fontSize: 12, color: 'error.main' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ));
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!isLoading && totalEntries === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 12 }}>
              No appointments scheduled for this date.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Entry detail dialog */}
      {selectedEntry && (
        <Dialog open onClose={() => setSelectedEntry(null)} maxWidth="xs" fullWidth
          PaperProps={{ sx: { borderRadius: '6px', border: `1px solid ${gwtStyles.borderSubtle}`, backgroundColor: gwtStyles.popupBg } }}>
          <DialogTitle sx={{ fontSize: 14, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: gwtStyles.wizardDetail }}>
            Appointment Details
            <IconButton size="small" onClick={() => setSelectedEntry(null)} sx={{ width: 24, height: 24 }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ py: 2 }}>
            <DetailRow l="Time" v={selectedEntry.time ?? '—'} />
            <DetailRow l="Patient" v={selectedEntry.patientName ?? '—'} />
            <DetailRow l="Modality" v={selectedEntry.modality ?? '—'} />
            <DetailRow l="Description" v={selectedEntry.description ?? '—'} />
            <DetailRow l="Status" v={selectedEntry.status ?? '—'} />
            <DetailRow l="Duration" v={selectedEntry.duration ? `${selectedEntry.duration} min` : '—'} />
            <DetailRow l="Site" v={selectedEntry.siteId ?? '—'} />
            <DetailRow l="Referring" v={selectedEntry.referringPhysician ?? '—'} />
          </DialogContent>
          <DialogActions sx={{ px: 2, pb: 1.5 }}>
            <Button size="small" onClick={() => { setSelectedEntry(null); setRescheduleTarget(selectedEntry); }} sx={{ textTransform: 'none', fontSize: 12 }}>
              Reschedule
            </Button>
            <Button size="small" color="error" onClick={() => { setSelectedEntry(null); setUnscheduleTarget(selectedEntry); }} sx={{ textTransform: 'none', fontSize: 12 }}>
              Unschedule
            </Button>
            <Button size="small" onClick={() => setSelectedEntry(null)} sx={{ textTransform: 'none', fontSize: 12 }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Unschedule confirm */}
      {unscheduleTarget && (
        <ConfirmDialog
          open title="Unschedule Order"
          message={`Unschedule "${unscheduleTarget.patientName} — ${unscheduleTarget.description}" at ${unscheduleTarget.time}? The order will return to ORDERED status.`}
          destructive onConfirm={handleUnschedule} onCancel={() => setUnscheduleTarget(null)}
        />
      )}

      {/* Reschedule dialog */}
      {rescheduleTarget && (
        <RescheduleDialog
          entry={rescheduleTarget}
          open
          onClose={() => setRescheduleTarget(null)}
          onSaved={() => { setRescheduleTarget(null); refresh(); }}
        />
      )}

      {/* Add Appointment dialog */}
      {showAddDialog && (
        <AddAppointmentDialog
          open
          defaultDate={selectedDate}
          onClose={() => setShowAddDialog(false)}
          onSaved={() => { setShowAddDialog(false); refresh(); }}
        />
      )}
    </Box>
  );
};

/* ── Add Appointment Dialog ── */
const AddAppointmentDialog = ({ open, defaultDate, onClose, onSaved }: {
  open: boolean; defaultDate: string; onClose: () => void; onSaved: () => void;
}) => {
  const [patientSearch, setPatientSearch] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<PatientRecord | null>(null);
  const [modality, setModality] = useState('XR');
  const [description, setDescription] = useState('');
  const [apptDate, setApptDate] = useState(defaultDate);
  const [apptTime, setApptTime] = useState('09:00');
  const [duration, setDuration] = useState('30');
  const [saving, setSaving] = useState(false);

  // Patient search autocomplete
  const { data: patientResults, isFetching: patientsLoading } = useQuery({
    queryKey: ['patientSearch', patientSearch],
    queryFn: async () => {
      if (patientSearch.length < 2) return [];
      const res = await api.getPatients(0, 20, patientSearch);
      return res.records ?? [];
    },
    enabled: patientSearch.length >= 2,
    staleTime: 10_000,
  });

  const handleSave = useCallback(async () => {
    if (!selectedPatient) {
      pushError('Please select a patient');
      return;
    }
    if (!description.trim()) {
      pushError('Please enter a procedure description');
      return;
    }
    setSaving(true);
    try {
      // 1. Create order via addOrders
      const orderInfo = {
        detailOrder: {
          universalServiceId: modality,
          universalServiceIdDescr: description.trim(),
          procedureCodeDescr: description.trim(),
          priority: 'R',
        },
      };
      const studies = await api.addOrders(selectedPatient.objKey, [orderInfo], [{ mod: modality }]);
      if (!studies || studies.length === 0) {
        pushError('Failed to create order — no study returned');
        setSaving(false);
        return;
      }
      // 2. Schedule the created order
      const dateMs = new Date(`${apptDate}T${apptTime}:00`).getTime();
      await api.scheduleOrder(studies[0].objKey, dateMs, undefined, parseInt(duration, 10) || 30);
      pushSuccess('Appointment created and scheduled');
      onSaved();
    } catch (e) {
      pushError(`Failed to create appointment: ${e instanceof Error ? e.message : String(e)}`);
    }
    setSaving(false);
  }, [selectedPatient, modality, description, apptDate, apptTime, duration, onSaved]);

  const canSave = selectedPatient && description.trim().length > 0 && !saving;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: '6px', border: `1px solid ${gwtStyles.borderSubtle}`, backgroundColor: gwtStyles.popupBg } }}>
      <DialogTitle sx={{ fontSize: 14, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: gwtStyles.wizardDetail }}>
        Add Appointment
        <IconButton size="small" onClick={onClose} sx={{ width: 24, height: 24 }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ py: 2 }}>
        {/* Patient search */}
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" sx={{ fontSize: 12, mb: 0.5, fontWeight: 600 }}>Patient *</Typography>
          <Autocomplete
            size="small"
            options={patientResults ?? []}
            loading={patientsLoading}
            getOptionLabel={(p: PatientRecord) => `${p.name ?? ''} (${p.patientId ?? ''})`}
            isOptionEqualToValue={(a, b) => a.objKey === b.objKey}
            onInputChange={(_, val) => setPatientSearch(val)}
            onChange={(_, val) => setSelectedPatient(val)}
            renderInput={(params) => (
              <TextField {...params} placeholder="Search by name or ID..."
                InputProps={{ ...params.InputProps, endAdornment: (
                  <>
                    {patientsLoading ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                )}}
                sx={{ '& .MuiInputBase-root': { fontSize: 12 } }} />
            )}
            renderOption={(props, p) => (
              <li {...props} key={p.objKey}>
                <Box>
                  <Typography sx={{ fontSize: 12 }}>{p.name}</Typography>
                  <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                    ID: {p.patientId ?? '—'} | DOB: {p.dob ?? '—'} | Sex: {p.sex ?? '—'}
                  </Typography>
                </Box>
              </li>
            )}
          />
        </Box>

        {/* Modality + Description */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
          <Box sx={{ width: 120 }}>
            <Typography variant="body2" sx={{ fontSize: 12, mb: 0.5, fontWeight: 600 }}>Modality</Typography>
            <Select size="small" value={modality} onChange={(e) => setModality(e.target.value)} fullWidth
              sx={{ fontSize: 12, height: 32 }}>
              {['CT', 'MR', 'XR', 'US', 'NM', 'MG', 'PET', 'DEXA'].map((m) => (
                <MenuItem key={m} value={m} sx={{ fontSize: 12 }}>{m}</MenuItem>
              ))}
            </Select>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontSize: 12, mb: 0.5, fontWeight: 600 }}>Procedure / Description *</Typography>
            <TextField size="small" fullWidth value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Chest X-Ray 2-View"
              sx={{ '& .MuiInputBase-root': { fontSize: 12, height: 32 } }} />
          </Box>
        </Box>

        {/* Date + Time + Duration */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
          <Box>
            <Typography variant="body2" sx={{ fontSize: 12, mb: 0.5, fontWeight: 600 }}>Date</Typography>
            <TextField size="small" type="date" value={apptDate} onChange={(e) => setApptDate(e.target.value)}
              sx={{ '& .MuiInputBase-root': { fontSize: 12, height: 32 }, width: 150 }} />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontSize: 12, mb: 0.5, fontWeight: 600 }}>Time</Typography>
            <TextField size="small" type="time" value={apptTime} onChange={(e) => setApptTime(e.target.value)}
              sx={{ '& .MuiInputBase-root': { fontSize: 12, height: 32 }, width: 120 }} />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontSize: 12, mb: 0.5, fontWeight: 600 }}>Duration (min)</Typography>
            <TextField size="small" type="number" value={duration} onChange={(e) => setDuration(e.target.value)}
              inputProps={{ min: 5, max: 480, step: 5 }}
              sx={{ '& .MuiInputBase-root': { fontSize: 12, height: 32 }, width: 90 }} />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Button size="small" onClick={onClose} sx={{ textTransform: 'none', fontSize: 12 }}>Cancel</Button>
        <Button size="small" variant="contained" onClick={handleSave} disabled={!canSave}
          sx={{ textTransform: 'none', fontSize: 12 }}>
          {saving ? 'Scheduling...' : 'Schedule Appointment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ── Reschedule Dialog ── */
const RescheduleDialog = ({ entry, open, onClose, onSaved }: {
  entry: ScheduleEntry; open: boolean; onClose: () => void; onSaved: () => void;
}) => {
  const [newDate, setNewDate] = useState(() => {
    // Try to extract date from entry
    return new Date().toISOString().split('T')[0];
  });
  const [newTime, setNewTime] = useState(entry.time ?? '09:00');
  const [duration, setDuration] = useState(String(entry.duration ?? 30));
  const [saving, setSaving] = useState(false);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      // Convert date + time to epoch ms
      const dateMs = new Date(`${newDate}T${newTime}:00`).getTime();
      await api.rescheduleOrder({
        objKey: entry.objKey ?? entry.id,
        date: dateMs,
        action: 'move',
        duration: parseInt(duration, 10) || 30,
      });
      pushSuccess('Order rescheduled');
      onSaved();
    } catch (e) {
      pushError(`Failed to reschedule: ${e instanceof Error ? e.message : String(e)}`);
    }
    setSaving(false);
  }, [entry, newDate, newTime, duration, onSaved]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: '6px', border: `1px solid ${gwtStyles.borderSubtle}`, backgroundColor: gwtStyles.popupBg } }}>
      <DialogTitle sx={{ fontSize: 14, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: gwtStyles.wizardDetail }}>
        Reschedule — {entry.patientName}
        <IconButton size="small" onClick={onClose} sx={{ width: 24, height: 24 }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ py: 2 }}>
        <DetailRow l="Current Time" v={entry.time ?? '—'} />
        <DetailRow l="Procedure" v={entry.description ?? '—'} />
        <Box sx={{ my: 1.5, borderTop: '1px solid', borderColor: 'divider' }} />
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.75, gap: 1 }}>
          <Typography variant="body2" sx={{ fontSize: 12, minWidth: 90, textAlign: 'right', pt: 0.5 }}>New Date:</Typography>
          <TextField size="small" type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} sx={{ '& .MuiInputBase-root': { fontSize: 12 } }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.75, gap: 1 }}>
          <Typography variant="body2" sx={{ fontSize: 12, minWidth: 90, textAlign: 'right', pt: 0.5 }}>New Time:</Typography>
          <TextField size="small" type="time" value={newTime} onChange={(e) => setNewTime(e.target.value)} sx={{ '& .MuiInputBase-root': { fontSize: 12 } }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.75, gap: 1 }}>
          <Typography variant="body2" sx={{ fontSize: 12, minWidth: 90, textAlign: 'right', pt: 0.5 }}>Duration (min):</Typography>
          <TextField size="small" type="number" value={duration} onChange={(e) => setDuration(e.target.value)} sx={{ width: 80, '& .MuiInputBase-root': { fontSize: 12 } }} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Button size="small" onClick={onClose} sx={{ textTransform: 'none', fontSize: 12 }}>Cancel</Button>
        <Button size="small" variant="contained" onClick={handleSave} disabled={saving} sx={{ textTransform: 'none', fontSize: 12 }}>
          {saving ? 'Saving...' : 'Reschedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ── Shared ── */
const DetailRow = ({ l, v }: { l: string; v: string }) => (
  <Box sx={{ display: 'flex', mb: 0.5, gap: 1 }}>
    <Typography variant="body2" sx={{ fontSize: 12, minWidth: 90, textAlign: 'right', color: 'text.secondary' }}>{l}:</Typography>
    <Typography variant="body2" sx={{ fontSize: 12 }}>{v}</Typography>
  </Box>
);
